import { Button, Group, List, Stack, Text } from '@mantine/core';

import { Batch, EnvironmentCountries, ReportStatus } from '@e-origin/shared';

import { useDownloadBulkUtbReport } from '@hooks/batch';
import { useSettings } from '@hooks/useSettings';
import { isUtbReportAvailable } from '../UTBReport';

export interface GenerateBulkUtbReportModalProps {
  onClose: () => void;
  selectedBatchIds: string[];
  batchesData: Batch[];
}

export const GenerateBulkUtbReportModal: React.FC<GenerateBulkUtbReportModalProps> = ({ onClose, selectedBatchIds, batchesData }) => {
  const { data: settings } = useSettings();
  const downloadReports = useDownloadBulkUtbReport();

  const selectedBatches = batchesData.filter((batch) => selectedBatchIds.includes(batch._id));
  const eligibleBatches = selectedBatches.filter((batch) => isUtbReportAvailable(batch, settings));
  const downloadableBatches = eligibleBatches.filter((batch) => batch.reports?.utbReport?.status === ReportStatus.DONE);
  const pendingBatches = eligibleBatches.filter((batch) => batch.reports?.utbReport?.status !== ReportStatus.DONE);
  const reportName = settings.country === EnvironmentCountries.NL ? 'UTB' : 'Customs Debt';

  const handleDownload = () => {
    downloadReports.mutate({ batchIds: downloadableBatches.map((batch) => batch._id) }, { onSuccess: onClose });
  };

  return (
    <Stack>
      {pendingBatches.length > 0 && (
        <Stack gap="xs">
          <Text c="orange">The {reportName} report for the following batches is not ready yet. It is generated automatically once the batch is finalized:</Text>
          <List>
            {pendingBatches.map((batch) => (
              <List.Item key={batch._id}>{batch.name}</List.Item>
            ))}
          </List>
        </Stack>
      )}

      {downloadableBatches.length > 0 ? (
        <Stack>
          <Text>{downloadableBatches.length} report(s) are ready. Do you want to download them?</Text>
          <Group justify="center">
            <Button type="button" variant="transparent" size="lg" onClick={onClose} disabled={downloadReports.isPending}>
              Cancel
            </Button>
            <Button type="button" variant="filled" size="lg" onClick={handleDownload} loading={downloadReports.isPending}>
              Download reports
            </Button>
          </Group>
        </Stack>
      ) : (
        <Text>None of the selected batches have a ready {reportName} report yet.</Text>
      )}
    </Stack>
  );
};
